import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Alert, PodsumowanieStanow, WierszStanu, Partia, Lista } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import { Karta, Komunikat, Plakietka, Przycisk, Pusto } from '../components/ui';
import { STATUS_STANU } from '../lib/slowniki';
import { PILNOSC, RODZAJ_ALERTU } from '../lib/slowniki-serwis';
import { data as fData, dataGodzina, ilosc, zlote } from '../lib/format';

/**
 * Pulpit — następca arkusza „Dashboard”. Liczniki te same co w Excelu
 * (liczba pozycji, sztuki, wartość, poniżej minimum, braki), ale liczone
 * z księgi ruchów i odświeżane same. Doszły dwie listy, których w arkuszu nie
 * dało się zrobić: co trzeba domówić i czemu kończy się termin.
 */
const ETAPY: { nr: number; nazwa: string; stan: 'zrobione' | 'nastepny' | 'plan' }[] = [
  { nr: 1, nazwa: 'Analiza arkusza, model danych, ustalenia', stan: 'zrobione' },
  { nr: 2, nazwa: 'Szkielet, baza, logowanie, role, dziennik', stan: 'zrobione' },
  { nr: 3, nazwa: 'Kartoteka, lokalizacje, partie i terminy ważności', stan: 'zrobione' },
  { nr: 4, nazwa: 'Ruchy PZ/WZ, korekty i storna, stany, FEFO', stan: 'zrobione' },
  { nr: 5, nazwa: 'Zajęcia, zestawy, rezerwacje, zwroty', stan: 'zrobione' },
  { nr: 6, nazwa: 'Przeglądy, inwentaryzacja, etykiety QR', stan: 'zrobione' },
  { nr: 7, nazwa: 'Raporty, eksporty, alerty na pulpicie', stan: 'zrobione' },
  { nr: 8, nazwa: 'Docker, kopie zapasowe, dokumentacja, testy', stan: 'nastepny' },
];

export function Pulpit() {
  const { uzytkownik, maUprawnienie } = useAuth();
  const widziCeny = maUprawnienie('ceny.odczyt');

  const podsumowanie = useQuery({
    queryKey: ['stany', 'podsumowanie'],
    queryFn: () => api.get<PodsumowanieStanow>('/stany/podsumowanie'),
    refetchInterval: 30_000,
  });

  const doZamowienia = useQuery({
    queryKey: ['stany', 'do-zamowienia'],
    queryFn: () => api.get<{ pozycje: WierszStanu[] }>('/stany?status=UZUPELNIJ&naStronie=10'),
    refetchInterval: 60_000,
  });

  const braki = useQuery({
    queryKey: ['stany', 'braki'],
    queryFn: () => api.get<{ pozycje: WierszStanu[] }>('/stany?status=BRAK_STANU&naStronie=10'),
    refetchInterval: 60_000,
  });

  const terminy = useQuery({
    queryKey: ['partie', 'pulpit-30'],
    queryFn: () => api.get<Lista<Partia>>('/partie?waznoscDoDni=30&naStronie=10'),
    refetchInterval: 60_000,
  });

  const alerty = useQuery({
    queryKey: ['serwis', 'alerty'],
    queryFn: () => api.get<{ pozycje: Alert[]; podsumowanie: Record<string, number> }>('/serwis/alerty'),
    refetchInterval: 60_000,
    enabled: maUprawnienie('serwis.odczyt'),
  });

  const przeterminowane = useQuery({
    queryKey: ['partie', 'pulpit-po-terminie'],
    queryFn: () => api.get<Lista<Partia>>('/partie?tylkoPrzeterminowane=true&naStronie=10'),
    refetchInterval: 60_000,
  });

  const p = podsumowanie.data;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">
            Dzień dobry, {uzytkownik?.fullName}
          </h1>
          <p className="mt-1 text-sm text-kamien-500">
            Rola: {uzytkownik?.roleNazwa} · {uzytkownik?.uprawnienia.length} uprawnień
          </p>
        </div>
        {maUprawnienie('ruch.przyjecie', 'ruch.wydanie') && (
          <div className="flex gap-2">
            <Link to="/dokumenty/nowy?typ=PZ"><Przycisk>Przyjęcie PZ</Przycisk></Link>
            <Link to="/dokumenty/nowy?typ=WZ"><Przycisk wariant="glowny">Wydanie WZ</Przycisk></Link>
          </div>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-kamien-200 ring-1 ring-kamien-200 sm:grid-cols-3 lg:grid-cols-5">
        <Kafelek etykieta="Pozycji w kartotece" wartosc={p ? String(p.towarow) : '—'} />
        <Kafelek etykieta="Sztuk na stanie" wartosc={p ? ilosc(p.sztuk) : '—'} />
        {widziCeny && <Kafelek etykieta="Wartość magazynu" wartosc={p ? zlote(p.wartosc) : '—'} />}
        <Kafelek etykieta="Poniżej minimum" wartosc={p ? String(p.ponizejMinimum) : '—'}
          ton={p && p.ponizejMinimum > 0 ? 'uwaga' : undefined} odnosnik="/stany?status=UZUPELNIJ" />
        <Kafelek etykieta="Braki" wartosc={p ? String(p.braki) : '—'}
          ton={p && p.braki > 0 ? 'blad' : undefined} odnosnik="/stany?status=BRAK_STANU" />
      </dl>

      {przeterminowane.data && przeterminowane.data.razem > 0 && (
        <Komunikat rodzaj="blad" tytul="Materiał po terminie ważności">
          {przeterminowane.data.razem === 1
            ? 'Jedna partia jest po terminie i nie wyjdzie z magazynu bez zgody kierownika.'
            : `${przeterminowane.data.razem} partie są po terminie i nie wyjdą z magazynu bez zgody kierownika.`}{' '}
          <Link to="/partie" className="underline underline-offset-2">Zobacz listę</Link>
        </Komunikat>
      )}

      {alerty.data && alerty.data.pozycje.filter((a) => a.pilnosc === 'ZALEGLY' || a.pilnosc === 'PILNY').length > 0 && (
        <Karta tytul="Wymaga uwagi" opis="Terminy, które minęły albo miną lada dzień">
          <ul className="divide-y divide-kamien-200">
            {alerty.data.pozycje
              .filter((a) => a.pilnosc === 'ZALEGLY' || a.pilnosc === 'PILNY')
              .slice(0, 8)
              .map((a, i) => (
                <li key={`${a.rodzaj}-${i}`} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                  <Plakietka ton={PILNOSC[a.pilnosc].ton}>
                    {a.dni < 0 ? `${Math.abs(a.dni)} dni po terminie` : `za ${a.dni} dni`}
                  </Plakietka>
                  <span className="text-xs uppercase tracking-wide text-kamien-400">
                    {RODZAJ_ALERTU[a.rodzaj]}
                  </span>
                  <span className="flex-1 text-kamien-900">{a.tytul}</span>
                  <span className="text-xs text-kamien-500">{a.opis}</span>
                </li>
              ))}
          </ul>
        </Karta>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Karta
          tytul="Do domówienia" opis="Pozycje na minimum albo poniżej"
          akcje={maUprawnienie('raporty.odczyt') ? (
            <Link to="/raporty" className="text-sm font-medium text-bordo-700 hover:underline">
              Pełna lista zakupowa
            </Link>
          ) : undefined}
        >
          {doZamowienia.data && doZamowienia.data.pozycje.length > 0 ? (
            <ul className="divide-y divide-kamien-200">
              {doZamowienia.data.pozycje.map((w) => (
                <li key={w.itemId} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                  <span className="font-mono text-xs text-kamien-500">{w.code}</span>
                  <span className="flex-1 text-kamien-800">{w.name}</span>
                  <span className="text-kamien-600">
                    {ilosc(w.stan)} / min {ilosc(w.minQty)} {w.unit}
                  </span>
                  <Plakietka ton={STATUS_STANU[w.status].ton}>{STATUS_STANU[w.status].etykieta}</Plakietka>
                </li>
              ))}
            </ul>
          ) : (
            <Pusto tytul="Nic nie wymaga domówienia" opis="Wszystkie pozycje mają stan powyżej minimum." />
          )}
        </Karta>

        <Karta tytul="Kończące się terminy" opis="Partie z terminem w najbliższych 30 dniach">
          {terminy.data && terminy.data.pozycje.length > 0 ? (
            <ul className="divide-y divide-kamien-200">
              {terminy.data.pozycje.map((b) => (
                <li key={b.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                  <span className="font-mono text-xs text-kamien-500">{b.item.code}</span>
                  <span className="flex-1 text-kamien-800">{b.item.name}</span>
                  <span className="font-mono text-xs text-kamien-500">{b.lotNumber ?? '—'}</span>
                  <span className="text-kamien-700">{fData(b.expiryDate)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Pusto tytul="Żaden termin się nie kończy" opis="W najbliższych 30 dniach nic nie traci ważności." />
          )}
        </Karta>
      </div>

      {braki.data && braki.data.pozycje.length > 0 && (
        <Karta tytul="Braki" opis="Stan ujemny — wymaga wyjaśnienia">
          <ul className="divide-y divide-kamien-200">
            {braki.data.pozycje.map((w) => (
              <li key={w.itemId} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                <span className="font-mono text-xs text-kamien-500">{w.code}</span>
                <span className="flex-1 text-kamien-800">{w.name}</span>
                <span className="font-medium text-status-brak">{ilosc(w.stan)} {w.unit}</span>
              </li>
            ))}
          </ul>
        </Karta>
      )}

      <Karta tytul="Postęp prac" opis="Etapy zgodnie z ustaleniami">
        <ol className="divide-y divide-kamien-200">
          {ETAPY.map((e) => (
            <li key={e.nr} className="flex items-center gap-4 px-5 py-2.5">
              <span className="w-6 text-sm text-kamien-400">{String(e.nr).padStart(2, '0')}</span>
              <span className={`flex-1 text-sm ${e.stan === 'plan' ? 'text-kamien-400' : 'text-kamien-800'}`}>
                {e.nazwa}
              </span>
              {e.stan === 'zrobione' && <Plakietka ton="ok">gotowe</Plakietka>}
              {e.stan === 'nastepny' && <Plakietka ton="uwaga">następny</Plakietka>}
            </li>
          ))}
        </ol>
      </Karta>

      <p className="text-xs text-kamien-400">
        Liczniki odświeżają się co 30 sekund. Ostatnio: {dataGodzina(new Date())}
      </p>
    </div>
  );
}

function Kafelek({ etykieta, wartosc, ton, odnosnik }: {
  etykieta: string; wartosc: string; ton?: 'uwaga' | 'blad'; odnosnik?: string;
}) {
  const kolor = ton === 'blad' ? 'text-status-brak' : ton === 'uwaga' ? 'text-status-uzupelnij' : 'text-kamien-900';
  const tresc = (
    <div className="h-full bg-white px-4 py-3 transition-colors hover:bg-kamien-50">
      <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className={`mt-0.5 text-xl font-semibold ${kolor}`}>{wartosc}</dd>
    </div>
  );
  return odnosnik ? <Link to={odnosnik}>{tresc}</Link> : tresc;
}
